import { Logger, withPrefix } from '@gitlab-org/logging';
import { Injectable } from '@gitlab/needle';
import { CustomSlashCommandResolver } from '@gitlab-org/ai-context/node';
import type { ControllerApi } from '../../commands/tui/controller_api';
import { UserActionType } from '../../backend/backend';
import { SlashCommandHandler, SlashCommandAction } from '../slash_command_handler';

@Injectable(SlashCommandHandler, [Logger, CustomSlashCommandResolver])
export class DefaultCustomCommandHandler implements SlashCommandHandler {
  #logger: Logger;

  #resolver: CustomSlashCommandResolver;

  constructor(logger: Logger, resolver: CustomSlashCommandResolver) {
    this.#logger = withPrefix(logger, '[CustomCommandHandler]');
    this.#resolver = resolver;
  }

  // Placeholder command: every dynamic custom command routes to this single
  // handler via SlashCommandAction.CustomCommand. Marked internal so it never
  // surfaces in the command list or autocomplete.
  command = {
    name: '/custom-command',
    description: 'Run a custom slash command.',
    action: SlashCommandAction.CustomCommand,
    internal: true,
  } as const;

  async toPrompt(args?: string[]): Promise<string | undefined> {
    const commandName = args?.[0];
    if (!commandName) {
      this.#logger.warn('Custom command invoked without a name');
      return undefined;
    }

    const { commands } = await this.#resolver.getCustomSlashCommands();
    const command = commands.find((c) => c.name === commandName);
    if (!command) {
      this.#logger.warn(`Custom command "${commandName}" not found`);
      return undefined;
    }

    const extraArgs = args.slice(1).join(' ').trim();
    return extraArgs ? `${command.body}\n${extraArgs}` : command.body;
  }

  async execute(api: ControllerApi, args?: string[]): Promise<void> {
    const prompt = await this.toPrompt(args);
    if (prompt === undefined) return;

    this.#logger.info(`Executing custom command: ${args?.[0]}`);
    await api.sendPrompt({
      type: UserActionType.SendPrompt,
      prompt,
    });
  }
}
